import { useEffect, useMemo, useRef, useState } from 'react';
import { BusinessRegistry, type BusinessRegistryResult } from '@paladin-monorepo/common';
import API from '@/lib/api';
import { debounce } from '@/utils/debounce';

export interface UseBusinessRegistrySearchOptions {
  debounceMs?: number;
  minQueryLength?: number;
  lookupEnabled?: boolean;
  onSearchError?: (error: unknown) => void;
  onSearchSuccess?: (results: BusinessRegistryResult[]) => void;
}

/** Registries where an 8-digit query is an ICO (business id) lookup. */
const ICO_REGISTRIES: BusinessRegistry[] = [BusinessRegistry.CZ, BusinessRegistry.SK];

/** Registries where a 10-digit query is a NIP/KRS lookup. */
const NIP_REGISTRIES: BusinessRegistry[] = [BusinessRegistry.PL];

/**
 * Country-aware company lookup against GET /registry.
 * Not usable for BusinessRegistry.OTHER (globe) - that option performs no lookup.
 */
export function useBusinessRegistrySearch(
  country: BusinessRegistry,
  {
    debounceMs = 400,
    minQueryLength = 3,
    lookupEnabled = true,
    onSearchError,
    onSearchSuccess,
  }: UseBusinessRegistrySearchOptions = {},
) {
  const [results, setResults] = useState<BusinessRegistryResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const skipNextSearchRef = useRef(false);

  const search = useMemo(
    () =>
      debounce((query: string) => {
        if (skipNextSearchRef.current) {
          skipNextSearchRef.current = false;
          return;
        }
        if (
          !lookupEnabled
          || !query
          || query.length < minQueryLength
          || country === BusinessRegistry.OTHER
        ) {
          setResults([]);
          return;
        }

        setIsSearching(true);
        const params = new URLSearchParams();
        params.append('country', country);
        if (ICO_REGISTRIES.includes(country) && /^[0-9]{8}$/.test(query)) {
          params.append('companyId', query);
        } else if (NIP_REGISTRIES.includes(country) && /^[0-9]{10}$/.test(query)) {
          params.append('companyId', query);
        } else {
          params.append('companyName', query);
        }

        API.get<BusinessRegistryResult[]>(`/registry?${params.toString()}`)
          .then((response) => {
            setResults(response.data);
            onSearchSuccess?.(response.data);
          })
          .catch((error) => {
            setResults([]);
            onSearchError?.(error);
          })
          .finally(() => {
            setIsSearching(false);
          });
      }, debounceMs),
    [country, debounceMs, lookupEnabled, minQueryLength, onSearchError, onSearchSuccess],
  );

  useEffect(() => () => search.cancel(), [search]);

  return {
    results,
    setResults,
    isSearching,
    search,
    skipNextSearchRef,
  };
}
